import { FormGroup, ValidatorFn, AbstractControl, ValidationErrors, UntypedFormArray, UntypedFormControl, Validators, FormControl } from '@angular/forms';

function isEmptyInputValue(value: any): boolean {
  let isString: boolean = typeof (value) === 'string';
  // we don't check for string here so it also works with arrays
  return value == null || value.length === 0 || (isString && (<string>value).trim().length === 0);
}


export class NESSValidators extends Validators {

  static required(control: AbstractControl): ValidationErrors | null {
    return isEmptyInputValue(control.value) || (typeof (control.value) === 'string' && control.value.trim().length === 0) ? { 'required': true } : null;
  }

  static requireDisabled: ValidatorFn = (fg: FormGroup): ValidationErrors | null => {
    let isValid: boolean = true;

    for (let ctrl in fg.controls) {
      if (fg.controls[ctrl].validator) {
        isValid = !isEmptyInputValue(fg.controls[ctrl].value)
        if (!isValid)
          break;
      }
    }
    
    return isValid ? null : { 'requireDisabled': true };
  };

  static longLatPatternValid(longLatRegEx: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (control.value === null) {
        return null;
      }
      const isValid = longLatRegEx.test(control.value.trim());
      return isValid ? null : { longLatValid: true };
    };
  }

  static checkboxGroupRequired(minRequired: number = 1): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      let checked = 0;
      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.controls[key];
        if (control.value === true) {
          checked++;
        }
      });

      return (checked < minRequired) ? { checkboxGroupRequired: true } : null;
    }
  }

  static formArrayRequireOne(formArrayRequireOne: { value: string }): ValidatorFn {
    return (fa: UntypedFormArray): ValidationErrors | null => {
      let valid = fa.controls.length === 0 || false;
      let fields = formArrayRequireOne.value.split('.');
      for (let x = 0; x < fa.length; ++x) {
        let ctrl = fa.at(x)['controls'];
        for (let i = 0; i < fields.length; i++) {
          ctrl = ctrl[fields[i]];
        }

        if (!!ctrl.value) {
          valid = true;
          break;
        }
      }
      return valid ? null : {
        formArrayRequireOne: true
      };
    }
  }

  static multipleCheckboxRequireOne(fa: UntypedFormArray) {
    let valid = false;

    for (let x = 0; x < fa.length; ++x) {
      const ctrl = fa.at(x)['controls']['chbx'] || fa.at(x);
      if (ctrl.value) {
        valid = true;
        break;
      }
    }
    return valid ? null : {
      multipleCheckboxRequireOne: true
    };
  }

  static NFORCENumberValidator: ValidatorFn = (control: FormControl<string>): ValidationErrors | null => {
    let isValid: boolean = true;

    if (control && control.value) {
      isValid = control.value.indexOf('_') === -1;
    }
    return isValid ? null : { 'nforceNumberInvalid': true };
  };

  static passwordConfirmValidator(g: FormGroup) {
    return g.get('newPassword').value === g.get('confirmNewPassword').value ?
      null : { 'mismatch': true };
  }

  static passwordPolicyValidator(pwRegEx: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const policy = pwRegEx.test(control.value);
      return !policy ? { passwordPolicy: true } : null;
    };
  }

  static requiredIf(requiredIf: { value: boolean }): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value = control.value;
      if (requiredIf.value && isEmptyInputValue(value)) {
        return {
          requiredIf: { condition: requiredIf.value }
        };
      }
      return null;
    }
  }

  static atLeastOne(...fields: string[]) {
    return (fg: FormGroup): ValidationErrors | null => {
      return fields.some(fieldName => {
        const field = fg.get(fieldName).value;
        return !!field && !!field.length ? true : false;
      }) ? null
        : ({ atLeastOne: true } as ValidationErrors);
    };
  }

  static onlyOne(formArray: { value: string }) {
    return (fa: UntypedFormArray): ValidationErrors | null => {
      let valid = fa.controls.length === 0 || false;
      let fields = formArray.value.split('.');
      let ctrlCount: number = 0;
      for (let x = 0; x < fa.length; ++x) {
        let ctrl = fa.at(x)['controls'];
        for (let i = 0; i < fields.length; i++) {
          ctrl = ctrl[fields[i]];
        }

        if (ctrl.value == true) {
          ctrlCount += 1;
        }
      }

      valid = fa.controls.length === 0 || ctrlCount == 1;
      return valid ? null : {
        onlyOne: true
      };
    };
  }

  static conditionalRequired(predicate: any) {
    return (fc: UntypedFormControl): ValidationErrors | null => {
      if (!fc.parent) {
        return null;
      }

      if (predicate()) {
        return Validators.required(fc);
      }
      return null;
    };
  }

  static formArrayMaxLength(length: number) {
    return (fa: UntypedFormArray): ValidationErrors | null => {
      let valid = fa.controls.length <= length;
      return valid ? null : {
        formArrayMaxLength: true
      };
    }
  }

  static STSIDValidator1(ftsidRegEx: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (control.value === null) {
        return null;
      }
      const isValid = ftsidRegEx.test(control.value.trim());
      return isValid ? null : { STSIDValidator: true };
    };
  }

  static FTSIDValidator: ValidatorFn = (control: UntypedFormControl): ValidationErrors | null => {
    if (control.value == null) {
      return null;
    }
    const ftsidRegEx: RegExp = /(M|F|I|T)\d{11}\b/gmi;
    const isValid = ftsidRegEx.test(control.value.trim());
    return isValid ? null : { FTSIDValidatorInvalid: true };
  };

  static UniqueAlertName(validators: string[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (control.value === null) {
        return null;
      }
      const isValid = !validators.some(e => e?.toUpperCase() === control.value?.toUpperCase())
      return isValid ? null : { alertNameValid: true };
    };
  }
}

